import Axios, { AxiosResponse } from "axios";
import { CustomerInterface } from "../../_types/customer";
import { toggleLoading } from "../../state/creators";

const UNAUTH_KEY = process.env.UNAUTH_KEY;

// Agent configuration using agentkeepalive
const AGENT_CONFIG = {
	keepAlive: true,
	maxSockets: 200,
	freeSocketTimeout: 30000, // Only destroy sockets that are idle in the pool
};

let httpAgent;
let httpsAgent;

if (typeof window === "undefined") {
	const Agent = require("agentkeepalive");
	httpAgent = new Agent(AGENT_CONFIG);
	httpsAgent = new Agent.HttpsAgent(AGENT_CONFIG);
}

export const HeadersAuth = (TOKEN) => {
	const headers: any = {
		"Content-type": "application/json",
	};
	if (TOKEN) {
		headers.Authorization = `Bearer ${TOKEN}`;
	}
	return headers;
};

export const HeadersAuthBasic = (TOKEN, PRE_AUTH_TOKEN) => ({
	"Content-type": "application/json",
	Authorization: `Basic ${TOKEN}`,
	"Pre-Authorization": `Basic ${PRE_AUTH_TOKEN}`,
});

export const HeadersApiKey = (KEY) => ({
	"Content-type": "application/json",
	"Access-Key": KEY,
});

export const HeadersNoAuth = {
	"Content-type": "application/json",
};

export const HeadersKey = {
	"Content-type": "application/json",
	Authorization: `Bearer ${UNAUTH_KEY}`,
};

export interface IHttp {
	Method: string;
	Url: string;
	Data?: any;
	Headers?: HeadersInit;
	FormData?: boolean;
	Signal?: AbortSignal;
}

export interface IHttpResponse<D = any> {
	type: number;
	message?: string;
	data?: D;
	total?: number;
	totalActive?: number;
	errors?: any[];
	customer?: CustomerInterface;
	metadata?: any;
}

export interface IHttpError {
	status?: number;
	message?: string;
	name?: string;
}

export type GatewayHeaders = Record<string, string>;

type GatewayHeadersStorage = import("async_hooks").AsyncLocalStorage<GatewayHeaders | null>;

let gatewayHeadersStorage: GatewayHeadersStorage | null = null;

if (typeof window === "undefined") {
	const { AsyncLocalStorage } = require("async_hooks") as typeof import("async_hooks");
	gatewayHeadersStorage = new AsyncLocalStorage<GatewayHeaders | null>();
}

/** Run an SSR operation with gateway identity scoped to only this request. */
export const runWithSsrGatewayHeaders = <T>(headers: GatewayHeaders | null, fn: () => Promise<T>): Promise<T> => {
	return gatewayHeadersStorage ? gatewayHeadersStorage.run(headers, fn) : fn();
};

const getSsrGatewayHeaders = (): GatewayHeaders | null => {
	if (typeof window !== "undefined") {
		return null;
	}

	return gatewayHeadersStorage?.getStore() ?? null;
};

export const HTTP = ({ Method, Url, Data = null, Headers = {}, Signal = undefined }: IHttp) => {
	let headers = Headers["Content-type"] ? Headers : { ...Headers, "Content-type": "application/json" };
	const gatewayHeaders = getSsrGatewayHeaders();
	if (gatewayHeaders) {
		headers = { ...gatewayHeaders, ...headers };
	}
	const fetchOptions: any = {
		method: Method,
		headers,
		body: Data ? JSON.stringify(Data) : null,
		signal: Signal,
		// Auth is now gateway/cookie based; always send the auth session cookie.
		credentials: "include",
	};

	if (typeof window === "undefined") {
		// This code only runs on the server
		try {
			// Parse the URL to determine the protocol.
			const url = new URL(Url);
			// Ppply the agent for http and https protocols.
			if (url.protocol === "https:") {
				fetchOptions.agent = httpsAgent;
			} else if (url.protocol === "http:") {
				fetchOptions.agent = httpAgent;
			}
		} catch (error) {
			// Continue without a custom agent.
		}
	}

	return new Promise((resolve, reject) => {
		fetch(Url, fetchOptions)
			.then(async (res) => {
				const result = await res.json();
				if (res.ok) {
					resolve(result as IHttpResponse);
				} else {
					throw result as IHttpResponse;
				}
			})
			.catch(async (err) => {
				if (err?.name === "AbortError") {
					return;
				}
				if (err?.data?.logout && typeof window !== "undefined") {
					toggleLoading(false);
					setTimeout(() => {
						window.location.href = "/account/logout";
					}, 10);
				}
				reject(err);
			});
	});
};

export const HTTP_UPLOAD = ({ Method, Url, Data = null, Headers = {} }) => {
	return new Promise((resolve, reject) => {
		Axios({
			method: Method,
			url: Url,
			data: Data,
			headers: Headers,
			withCredentials: true,
		})
			.then(async (res) => {
				const result = res.data;
				if (result.type === 200) {
					resolve(result as IHttpResponse);
				}

				throw result as IHttpResponse;
			})
			.catch(async (err) => {
				reject((err?.response?.data as IHttpResponse) ?? err);
			});
	});
};

export const BLOB = ({ Method, Url, Data = null, Headers = {} }: IHttp) => {
	let headers = Headers["Content-type"] ? Headers : { ...Headers, "Content-type": "application/json" };
	return new Promise((resolve, reject) => {
		Axios({
			method: Method,
			responseType: "blob",
			url: Url,
			data: Data,
			headers,
			withCredentials: true,
		})
			.then(async (res) => {
				resolve(res as AxiosResponse<Blob>);
			})
			.catch(async (err) => {
				reject((err?.response?.data as IHttpResponse) ? JSON.parse(await err.response.data.text()) : err);
			});
	});
};

export const BUFFER = ({ Method, Url, Data = null, Headers = {} }: IHttp) => {
	let headers = Headers["Content-type"] ? Headers : { ...Headers, "Content-type": "application/json" };
	return new Promise((resolve, reject) => {
		Axios({
			method: Method,
			responseType: "arraybuffer",
			url: Url,
			data: Data,
			headers,
			withCredentials: true,
		})
			.then(async (res) => {
				resolve(res as AxiosResponse<Blob>);
			})
			.catch(async (err) => {
				reject(err);
			});
	});
};
export const serialiseError = (error: Error): IHttpError => {
	return {
		message: error?.message || "Unknown error",
		name: error?.name || "Error",
	};
};
